import React, { useState } from 'react'
import { InputNumber, Tooltip, Typography, Space } from 'antd'
import { InfoCircleOutlined, UndoOutlined } from '@ant-design/icons'
import { useUpdateManualValue } from '@/hooks/useRecviz'

const { Text } = Typography

export interface DualFieldCellProps {
  calculatedValue: number | null
  manualValue: number | null
  onChange: (value: number | null) => void
  editable?: boolean
  suffix?: string
  onInfoClick?: () => void
  precision?: number
  // Optional: if provided, uses dedicated manual value API instead of batch update
  departmentId?: string
  indicatorType?: 'ikz' | 'sdpp' | 'bed_work' | 'pct_planned'
  period?: string
  // For normative indicators (bed_work): source of the normative value
  normSource?: string | null
}

export const DualFieldCell: React.FC<DualFieldCellProps> = ({
  calculatedValue,
  manualValue,
  onChange,
  editable = true,
  suffix = '',
  onInfoClick,
  precision = 3,
  departmentId,
  indicatorType,
  period,
  normSource,
}) => {
  const [isEditing, setIsEditing] = useState(false)

  // Use dedicated manual value API if metadata is provided
  const updateManualValueMutation = useUpdateManualValue(period ?? '')

  // Определяем отображаемое значение (handle both null and undefined)
  const displayValue = manualValue != null ? manualValue : calculatedValue ?? null
  const isManual = manualValue != null
  const isNormative = normSource !== undefined && normSource !== null

  // Tooltip текст
  let tooltipText: string
  if (isNormative) {
    // For normative indicators (bed_work)
    if (isManual) {
      tooltipText = `Вручную: ${manualValue?.toFixed(precision)} ${suffix}\nНорматив: ${calculatedValue?.toFixed(precision) || 'N/A'} ${suffix}\nИсточник: ${normSource || 'не указан'}`
    } else {
      tooltipText = `Норматив: ${calculatedValue?.toFixed(precision) || 'N/A'} ${suffix}\nИсточник: ${normSource || 'не указан'}`
    }
  } else {
    // For calculated indicators (ikz, sdpp)
    tooltipText = isManual
      ? `Вручную: ${manualValue?.toFixed(precision)} ${suffix}\nРасчётное: ${calculatedValue?.toFixed(precision) || 'N/A'} ${suffix}`
      : `Расчётное: ${calculatedValue?.toFixed(precision) || 'N/A'} ${suffix}`
  }

  // Обработчик сброса manual значения
  const handleReset = (e: React.MouseEvent) => {
    e.stopPropagation()

    // Use dedicated API if available
    if (period && departmentId && indicatorType) {
      updateManualValueMutation.mutate({
        department_id: departmentId,
        indicator_type: indicatorType,
        value: null,
      })
    } else {
      // Fallback to onChange callback
      onChange(null)
    }
  }

  // Обработчик открытия модалки детализации
  const handleInfoClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    onInfoClick?.()
  }

  // Обработчик двойного клика для редактирования
  const handleDoubleClick = () => {
    if (editable) {
      setIsEditing(true)
    }
  }

  // Обработчик изменения значения
  const handleChange = (value: number | null) => {
    // Use dedicated API if available
    if (period && departmentId && indicatorType) {
      updateManualValueMutation.mutate({
        department_id: departmentId,
        indicator_type: indicatorType,
        value: value,
      })
    } else {
      // Fallback to onChange callback
      onChange(value)
    }
    setIsEditing(false)
  }

  // Обработчик blur (потеря фокуса)
  const handleBlur = () => {
    setIsEditing(false)
  }

  // Режим редактирования
  if (isEditing && editable) {
    return (
      <InputNumber
        autoFocus
        value={displayValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onPressEnter={() => setIsEditing(false)}
        precision={precision}
        style={{ width: '100%' }}
        suffix={suffix}
      />
    )
  }

  // Режим отображения
  return (
    <Tooltip title={tooltipText} placement="top">
      <div
        onDoubleClick={handleDoubleClick}
        style={{
          cursor: editable ? 'pointer' : 'default',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Space size={4}>
          <Text
            style={{
              color: isManual ? 'inherit' : '#999',
            }}
          >
            {displayValue !== null ? `${displayValue.toFixed(precision)} ${suffix}` : 'N/A'}
          </Text>

          {isManual ? (
            <UndoOutlined
              style={{ fontSize: '12px', color: '#1890ff', cursor: 'pointer' }}
              onClick={handleReset}
              title={isNormative ? "Сбросить до норматива" : "Сбросить до расчётного"}
            />
          ) : (
            <>
              {onInfoClick && (
                <InfoCircleOutlined
                  style={{ fontSize: '12px', color: '#999', cursor: 'pointer' }}
                  onClick={handleInfoClick}
                  title="Показать детализацию расчёта"
                />
              )}
              {isNormative && !onInfoClick && (
                <Tooltip title={`Источник норматива: ${normSource || 'не указан'}`} placement="top">
                  <InfoCircleOutlined
                    style={{ fontSize: '12px', color: '#1890ff' }}
                    title="Нормативный показатель"
                  />
                </Tooltip>
              )}
            </>
          )}
        </Space>
      </div>
    </Tooltip>
  )
}
